import React, { useState } from "react";

import Modal from "../../Modal";
import { Button } from "../../ui/button";
import { type SystemRole, createAdminUser } from "../../../services/adminApi";
import { inputCls } from "./shared";
import { Field } from "./Field";

export const AddUserModal: React.FC<{
  token: string;
  roles: SystemRole[];
  companies: { id: number; name: string }[];
  usersByCompanyType: (cid: number) => string | null;
  onClose: () => void;
  onDone: () => void;
}> = ({ token, roles, companies, usersByCompanyType, onClose, onDone }) => {
  const [companyId, setCompanyId] = useState<number | "">(companies.length === 1 ? companies[0].id : "");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [roleId, setRoleId] = useState<number | "">("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const ctype = companyId === "" ? null : usersByCompanyType(Number(companyId));
  const systemRoles = roles.filter((r) => !r.is_custom && (!ctype || r.company_type === ctype));
  const customRoles = roles.filter(
    (r) => r.is_custom && companyId !== "" && r.company_id === Number(companyId),
  );

  const save = async (): Promise<void> => {
    if (companyId === "") return setErr("Pick a company.");
    if (!email.includes("@")) return setErr("A valid email is required.");
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");
    if (roleId === "") return setErr("Pick a role.");
    setBusy(true);
    setErr(null);
    try {
      await createAdminUser(
        {
          company_id: Number(companyId),
          first_name: first || undefined,
          last_name: last || undefined,
          email: email.trim(),
          password: pw,
          role_id: Number(roleId),
        },
        token,
      );
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Add user" widthClass="max-w-lg">
      <div className="space-y-3">
        {err && <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{err}</div>}
        <Field label="Company">
          <select
            className={inputCls + " bg-white"}
            value={companyId}
            onChange={(e) => {
              setCompanyId(e.target.value === "" ? "" : Number(e.target.value));
              setRoleId("");
            }}
          >
            <option value="">Select…</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name">
            <input className={inputCls} value={first} onChange={(e) => setFirst(e.target.value)} />
          </Field>
          <Field label="Last name">
            <input className={inputCls} value={last} onChange={(e) => setLast(e.target.value)} />
          </Field>
        </div>
        <Field label="Email">
          <input className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Initial password (8+ chars)">
          <input
            type="password"
            className={inputCls}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
          />
        </Field>
        <Field label="Role">
          <select
            className={inputCls + " bg-white"}
            value={roleId}
            onChange={(e) => setRoleId(e.target.value === "" ? "" : Number(e.target.value))}
          >
            <option value="">Select…</option>
            {systemRoles.map((r) => (
              <option key={r.role_id} value={r.role_id}>
                {r.role_name}
              </option>
            ))}
            {customRoles.length > 0 && (
              <optgroup label="Custom roles">
                {customRoles.map((r) => (
                  <option key={r.role_id} value={r.role_id}>
                    {r.role_name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={busy} onClick={() => void save()}>
            {busy ? "Creating…" : "Create user"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
